<ng-container *ngIf="!loading; else isLoading">
  <!-- Error State -->
  <div *ngIf="error" class="row mb--md">
    <div class="column--12">
      <div class="error-container">
        <mat-icon color="warn">error</mat-icon>
        <p class="error-text">{{ error }}</p>
        <button mat-flat-button color="primary" (click)="handleError()">Retry</button>
      </div>
    </div>
  </div>

  <!-- Filters -->
  <form [formGroup]="form">
    <div class="row mb--md">
      <div class="column--12 column-md--8">
        <div class="filters">
          <mat-form-field appearance="outline" class="mr--md">
            <mat-label>Origin Province</mat-label>
            <mat-select formControlName="originProvince">
              <mat-option value="">All Provinces</mat-option>
              <mat-option *ngFor="let province of provinceOptions" [value]="province.id">{{
                province.text
              }}</mat-option>
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline" class="mr--md">
            <mat-label>Destination Province</mat-label>
            <mat-select formControlName="destinationProvince">
              <mat-option value="">All Provinces</mat-option>
              <mat-option *ngFor="let province of provinceOptions" [value]="province.id">{{
                province.text
              }}</mat-option>
            </mat-select>
          </mat-form-field>
        </div>
      </div>
    </div>
  </form>

  <!-- Main Content Section -->
  <div class="row mb--md">
    <!-- Left Column - Heatmap & Summary -->
    <div class="column--12 column-md--8">
      <!-- Heatmap Card -->
      <mat-card class="map-card">
        <mat-card-content class="card p--md">
          <mat-card-title>REGIONAL PERFORMANCE HEATMAP</mat-card-title>
          <div class="map-container">
            <app-canada-heatmap
              #heatMap
              [data]="locationData"
              [minRadius]="5"
              [maxRadius]="25"
              [maxZoom]="10"
              [detailZoomThreshold]="5"
              [tooltipFields]="tooltipFields"
              (provinceSelected)="onProvinceSelected($event)"
              (requestDetailData)="onRequestDetailData($event)"
            >
            </app-canada-heatmap>
          </div>
          <div *ngIf="selectedProvince" @fadeIn class="province-info mt--md">
            <h3>{{ selectedProvince }} Performance</h3>
            <div class="province-stats">
              <div class="stat-item">
                <span class="stat-label">Shipping Locations:</span>
                <span class="stat-value">{{ locationCount }}</span>
              </div>
            </div>
          </div>
        </mat-card-content>
      </mat-card>
    </div>

    <!-- Right Column - Summary & Metrics -->
    <div class="column--12 column-md--4">
      <!-- Combined Performance Summary & Risk Profile Card -->
      <mat-card class="summary-card">
        <mat-card-content class="card p--md">
          <mat-card-title>
            {{ selectedProvince ? selectedProvince + ' PERFORMANCE OVERVIEW' : 'PERFORMANCE SUMMARY' }}
          </mat-card-title>
          <div class="combined-metrics">
            <!-- First column: Performance metrics -->
            <div class="metrics-column">
              <div class="metric-group">
                <div class="metric-row">
                  <div class="metric-label">Total Shipments:</div>
                  <div class="metric-value">{{ totalShipments | number }}</div>
                </div>
                <div class="metric-row">
                  <div class="metric-label">On-Time Percentage:</div>
                  <div class="metric-value">{{ onTimeRate | number : '1.2-2' }}%</div>
                </div>
                <div class="metric-row">
                  <div class="metric-label">Average Delay:</div>
                  <div class="metric-value">{{ averageDelay | number : '1.2-2' }} hrs</div>
                </div>
              </div>
            </div>

            <!-- Second column: Claims metrics -->
            <div class="metrics-column" *ngIf="hasClaimsData">
              <div class="metric-group">
                <div class="metric-row">
                  <div class="metric-label">Total Claims:</div>
                  <div class="metric-value">{{ totalClaims | number }}</div>
                </div>
                <div class="metric-row">
                  <div class="metric-label">Claim Rate:</div>
                  <div class="metric-value" [ngStyle]="{ color: getClaimRateColor(claimRate) }">
                    {{ formatClaimRate(claimRate) }}
                  </div>
                </div>
                <div class="metric-row" *ngIf="selectedProvince && provinceClaimStats.highRiskRoutesCount > 0">
                  <div class="metric-label">High Risk Routes:</div>
                  <div class="metric-value">{{ provinceClaimStats.highRiskRoutesCount }}</div>
                </div>
              </div>
            </div>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </div>

  <!-- Top Routes Section -->
  <div class="row mb--md">
    <div class="column--12">
      <mat-card class="table-card">
        <mat-card-content class="card p--md">
          <mat-card-title>TOP PERFORMING ROUTES</mat-card-title>
          <div class="responsive-table">
            <table mat-table [dataSource]="topRoutes" class="mat-elevation-z8">
              <!-- Origin Column -->
              <ng-container matColumnDef="origin">
                <th mat-header-cell *matHeaderCellDef>ORIGIN</th>
                <td mat-cell *matCellDef="let route">
                  {{ route.route.origin.city }}, {{ route.route.origin.province }}
                </td>
              </ng-container>

              <!-- Destination Column -->
              <ng-container matColumnDef="destination">
                <th mat-header-cell *matHeaderCellDef>DESTINATION</th>
                <td mat-cell *matCellDef="let route">
                  {{ route.route.destination.city }}, {{ route.route.destination.province }}
                </td>
              </ng-container>

              <!-- Total Shipments Column -->
              <ng-container matColumnDef="totalShipments">
                <th mat-header-cell *matHeaderCellDef>SHIPMENTS</th>
                <td mat-cell *matCellDef="let route">
                  {{ route.data[route.data.length - 1].totalShipments | number }}
                </td>
              </ng-container>

              <!-- On-Time % Column -->
              <ng-container matColumnDef="onTimePercentage">
                <th mat-header-cell *matHeaderCellDef>ON-TIME %</th>
                <td mat-cell *matCellDef="let route">
                  {{ route.data[route.data.length - 1].onTimePercentage | number : '1.2-2' }}%
                </td>
              </ng-container>

              <!-- Average Delay Column -->
              <ng-container matColumnDef="averageDelay">
                <th mat-header-cell *matHeaderCellDef>AVG DELAY</th>
                <td mat-cell *matCellDef="let route">
                  {{ route.data[route.data.length - 1].averageDelay | number : '1.2-2' }} hrs
                </td>
              </ng-container>

              <!-- Claims Column (only show if claims data available) -->
              <ng-container matColumnDef="claims">
                <th mat-header-cell *matHeaderCellDef>CLAIMS</th>
                <td mat-cell *matCellDef="let route">
                  <ng-container *ngIf="route.data[route.data.length - 1].totalClaims !== undefined; else noClaimsData">
                    {{ route.data[route.data.length - 1].totalClaims | number }}
                    ({{ route.data[route.data.length - 1].claimRate | number : '1.2-2' }}%)
                  </ng-container>
                  <ng-template #noClaimsData>N/A</ng-template>
                </td>
              </ng-container>

              <!-- Actions Column -->
              <ng-container matColumnDef="actions">
                <th mat-header-cell *matHeaderCellDef>ACTIONS</th>
                <td mat-cell *matCellDef="let route">
                  <button mat-icon-button color="primary" (click)="showRouteDetails(route)" matTooltip="View Details">
                    <mat-icon>visibility</mat-icon>
                  </button>
                </td>
              </ng-container>

              <tr
                mat-header-row
                *matHeaderRowDef="
                  hasClaimsData
                    ? [
                        'origin',
                        'destination',
                        'totalShipments',
                        'onTimePercentage',
                        'averageDelay',
                        'claims',
                        'actions'
                      ]
                    : ['origin', 'destination', 'totalShipments', 'onTimePercentage', 'averageDelay', 'actions']
                "
              ></tr>
              <tr
                mat-row
                *matRowDef="
                  let row;
                  columns: hasClaimsData
                    ? [
                        'origin',
                        'destination',
                        'totalShipments',
                        'onTimePercentage',
                        'averageDelay',
                        'claims',
                        'actions'
                      ]
                    : ['origin', 'destination', 'totalShipments', 'onTimePercentage', 'averageDelay', 'actions']
                "
              ></tr>
            </table>
          </div>
          <div *ngIf="topRoutes.length === 0" class="no-data mt--md">
            <p>No route data available for the selected filters.</p>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </div>

  <!-- High Claim Routes Section -->
  <div class="row mb--md" *ngIf="regionData?.highClaimRoutes && regionData.highClaimRoutes.length > 0">
    <div class="column--12">
      <mat-card class="table-card" id="highClaimRoutes">
        <mat-card-content class="card p--md">
          <mat-card-title>HIGH CLAIM ROUTES</mat-card-title>
          <div class="responsive-table">
            <table mat-table [dataSource]="regionData.highClaimRoutes" class="mat-elevation-z8">
              <!-- Origin Column -->
              <ng-container matColumnDef="origin">
                <th mat-header-cell *matHeaderCellDef>ORIGIN</th>
                <td mat-cell *matCellDef="let route">
                  {{ route.route.origin.city }}, {{ route.route.origin.province }}
                </td>
              </ng-container>

              <!-- Destination Column -->
              <ng-container matColumnDef="destination">
                <th mat-header-cell *matHeaderCellDef>DESTINATION</th>
                <td mat-cell *matCellDef="let route">
                  {{ route.route.destination.city }}, {{ route.route.destination.province }}
                </td>
              </ng-container>

              <!-- Total Shipments Column -->
              <ng-container matColumnDef="totalShipments">
                <th mat-header-cell *matHeaderCellDef>SHIPMENTS</th>
                <td mat-cell *matCellDef="let route">
                  {{ route.data[route.data.length - 1].totalShipments | number }}
                </td>
              </ng-container>

              <!-- Claims Column -->
              <ng-container matColumnDef="claims">
                <th mat-header-cell *matHeaderCellDef>CLAIMS</th>
                <td mat-cell *matCellDef="let route">
                  {{ route.data[route.data.length - 1].totalClaims | number }}
                </td>
              </ng-container>

              <!-- Claim Rate Column -->
              <ng-container matColumnDef="claimRate">
                <th mat-header-cell *matHeaderCellDef>CLAIM RATE</th>
                <td mat-cell *matCellDef="let route">
                  <div class="metric-cell">
                    <div class="percentage-bar-container">
                      <div
                        class="percentage-bar"
                        [style.width]="route.data[route.data.length - 1].claimRate * 10 + '%'"
                        [ngClass]="getClaimRiskClass(route.data[route.data.length - 1].claimRate)"
                      ></div>
                    </div>
                    <div class="metric-value">
                      {{ route.data[route.data.length - 1].claimRate | number : '1.2-2' }}%
                    </div>
                  </div>
                </td>
              </ng-container>

              <!-- Actions Column -->
              <ng-container matColumnDef="actions">
                <th mat-header-cell *matHeaderCellDef>ACTIONS</th>
                <td mat-cell *matCellDef="let route">
                  <button
                    mat-icon-button
                    color="primary"
                    (click)="showHighClaimRouteDetails(route)"
                    matTooltip="View Details"
                  >
                    <mat-icon>visibility</mat-icon>
                  </button>
                </td>
              </ng-container>

              <tr
                mat-header-row
                *matHeaderRowDef="['origin', 'destination', 'totalShipments', 'claims', 'claimRate', 'actions']"
              ></tr>
              <tr
                mat-row
                *matRowDef="
                  let row;
                  columns: ['origin', 'destination', 'totalShipments', 'claims', 'claimRate', 'actions']
                "
              ></tr>
            </table>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </div>

  <!-- High Volume Routes Section -->
  <div class="row mb--md" *ngIf="regionData?.highVolumeRoutes && regionData.highVolumeRoutes.length > 0">
    <div class="column--12">
      <mat-card class="table-card" id="highVolumeRoutes">
        <mat-card-content class="card p--md">
          <mat-card-title>HIGH VOLUME ROUTES</mat-card-title>
          <div class="responsive-table">
            <table mat-table [dataSource]="regionData.highVolumeRoutes" class="mat-elevation-z8">
              <!-- Origin Column -->
              <ng-container matColumnDef="origin">
                <th mat-header-cell *matHeaderCellDef>ORIGIN</th>
                <td mat-cell *matCellDef="let route">
                  {{ route.route.origin.city }}, {{ route.route.origin.province }}
                </td>
              </ng-container>

              <!-- Destination Column -->
              <ng-container matColumnDef="destination">
                <th mat-header-cell *matHeaderCellDef>DESTINATION</th>
                <td mat-cell *matCellDef="let route">
                  {{ route.route.destination.city }}, {{ route.route.destination.province }}
                </td>
              </ng-container>

              <!-- Total Shipments Column -->
              <ng-container matColumnDef="totalShipments">
                <th mat-header-cell *matHeaderCellDef>SHIPMENTS</th>
                <td mat-cell *matCellDef="let route">
                  {{ route.data[route.data.length - 1].totalShipments | number }}
                </td>
              </ng-container>

              <!-- On-Time % Column -->
              <ng-container matColumnDef="onTimePercentage">
                <th mat-header-cell *matHeaderCellDef>ON-TIME %</th>
                <td mat-cell *matCellDef="let route">
                  {{ route.data[route.data.length - 1].onTimePercentage | number : '1.2-2' }}%
                </td>
              </ng-container>

              <!-- Average Delay Column -->
              <ng-container matColumnDef="averageDelay">
                <th mat-header-cell *matHeaderCellDef>AVG DELAY</th>
                <td mat-cell *matCellDef="let route">
                  {{ route.data[route.data.length - 1].averageDelay | number : '1.2-2' }} hrs
                </td>
              </ng-container>

              <!-- Actions Column -->
              <ng-container matColumnDef="actions">
                <th mat-header-cell *matHeaderCellDef>ACTIONS</th>
                <td mat-cell *matCellDef="let route">
                  <button
                    mat-icon-button
                    color="primary"
                    (click)="showHighVolumeRouteDetails(route)"
                    matTooltip="View Details"
                  >
                    <mat-icon>visibility</mat-icon>
                  </button>
                </td>
              </ng-container>

              <tr
                mat-header-row
                *matHeaderRowDef="[
                  'origin',
                  'destination',
                  'totalShipments',
                  'onTimePercentage',
                  'averageDelay',
                  'actions'
                ]"
              ></tr>
              <tr
                mat-row
                *matRowDef="
                  let row;
                  columns: ['origin', 'destination', 'totalShipments', 'onTimePercentage', 'averageDelay', 'actions']
                "
              ></tr>
            </table>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </div>

  <!-- Route Details Section (only shown when a route is selected) -->
  <div id="routeDetailsSection" class="row mb--md" *ngIf="showingRouteDetails && currentRouteMetrics" @expandCard>
    <div class="column--12">
      <mat-card class="route-details-card">
        <mat-card-content class="card p--md">
          <div class="route-header">
            <mat-card-title>
              ROUTE DETAILS: {{ currentRouteMetrics.origin.city }}, {{ currentRouteMetrics.origin.province }} to
              {{ currentRouteMetrics.destination.city }}, {{ currentRouteMetrics.destination.province }}
            </mat-card-title>
            <button mat-icon-button color="primary" (click)="closeRouteDetails()" matTooltip="Close Details">
              <mat-icon>close</mat-icon>
            </button>
          </div>

          <!-- Route Performance Metrics -->
          <div class="route-metrics">
            <div class="row">
              <div class="column--12 column-md--6">
                <!-- Performance Trend Chart -->
                <div class="chart-container">
                  <ngx-charts-line-chart
                    [results]="routeOnTimeData"
                    [xAxis]="true"
                    [yAxis]="true"
                    [legend]="true"
                    [showXAxisLabel]="true"
                    [showYAxisLabel]="true"
                    xAxisLabel="Time Period"
                    yAxisLabel="Percentage"
                    [yAxisTickFormatting]="formatYAxisPercentageTicks"
                    [scheme]="defaultColorScheme"
                  >
                  </ngx-charts-line-chart>
                </div>
              </div>
              <div class="column--12 column-md--6">
                <!-- Volume Chart -->
                <div class="chart-container">
                  <ngx-charts-bar-vertical
                    [results]="routeVolumeData"
                    [xAxis]="true"
                    [yAxis]="true"
                    [showXAxisLabel]="true"
                    [showYAxisLabel]="true"
                    xAxisLabel="Time Period"
                    yAxisLabel="Shipments"
                    [scheme]="defaultColorScheme"
                  >
                  </ngx-charts-bar-vertical>
                </div>
              </div>
            </div>

            <!-- Claims Data (if available) -->
            <div class="row mt--md" *ngIf="hasClaimsData && routeClaimsData.length > 0">
              <div class="column--12">
                <h3>Claims Analysis</h3>
                <div class="chart-container">
                  <ngx-charts-line-chart
                    [results]="routeClaimsData"
                    [xAxis]="true"
                    [yAxis]="true"
                    [legend]="true"
                    [showXAxisLabel]="true"
                    [showYAxisLabel]="true"
                    xAxisLabel="Time Period"
                    yAxisLabel="Claim Rate (%)"
                    [yAxisTickFormatting]="formatClaimRateTicks"
                    [scheme]="claimsColorScheme"
                  >
                  </ngx-charts-line-chart>
                </div>
              </div>
            </div>

            <!-- Courier Recommendations -->
            <div class="row mt--md">
              <div class="column--12">
                <h3>Courier Recommendations</h3>
                <div class="recommendations">
                  <div class="recommendation-item">
                    <div class="label">Best Performing Courier:</div>
                    <div class="value text-success">
                      {{ currentRoutePerformance[currentRoutePerformance.length - 1].bestCourier }}
                    </div>
                  </div>
                  <div class="recommendation-item">
                    <div class="label">Avoid:</div>
                    <div class="value text-danger">
                      {{ currentRoutePerformance[currentRoutePerformance.length - 1].worstCourier }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </div>
</ng-container>

<!-- Loading Template -->
<ng-template #isLoading>
  <div class="align-center mt--lg">
    <app-loading></app-loading>
  </div>
</ng-template>
